import MentionMediaThumbnail from '@/components/mentions/MentionMediaThumbnail';
import { useMentionQuery } from '@/hooks/queries/use-mention-query';
import { Skeleton } from '@chakra-ui/react';
import type { MentionType } from '@fluenceur/types';
import { AtSign } from 'iconoir-react';
import { parseAsString, useQueryState } from 'nuqs';
import { useTranslation } from 'react-i18next';
import SystemEventCard from './SystemEventCard';
import { useSystemEventLabel } from './use-system-event-label';

interface MentionEventCardProps {
  mentionId: string;
  mentionType: MentionType;
  occurredAt: string;
}

const MENTION_ICON = <AtSign width={13} height={13} />;

/**
 * The mention marker: the registry card with the media it refers to.
 *
 * The marker carries nothing but a reference, so the card reloads the mention
 * by that id — the same read the message rendering has always used. The title
 * comes from the marker itself and is therefore right before anything loads.
 */
const MentionEventCard = ({ mentionId, mentionType, occurredAt }: MentionEventCardProps) => {
  const { t } = useTranslation();
  const { mentionTitle } = useSystemEventLabel();
  const [, setPreviewMentionId] = useQueryState('previewMention', parseAsString);

  const { data: mention, isLoading, isError } = useMentionQuery({ mentionId });

  const title = mentionTitle(mentionType);

  if (isLoading) {
    return (
      <SystemEventCard
        icon={MENTION_ICON}
        title={title}
        occurredAt={occurredAt}
        media={<Skeleton boxSize="44px" />}
      />
    );
  }

  // The mention can be gone (expired, erased, never mirrored). The marker still
  // happened, so the card stays — inert, and saying so in one clause.
  if (isError || !mention) {
    return (
      <SystemEventCard
        icon={MENTION_ICON}
        title={title}
        details={t('messages.systemEvent.mentionUnavailable')}
        occurredAt={occurredAt}
      />
    );
  }

  return (
    <SystemEventCard
      icon={MENTION_ICON}
      title={title}
      details={mention.caption || undefined}
      occurredAt={occurredAt}
      media={<MentionMediaThumbnail mention={mention} openOnClick={false} />}
      onOpen={() => setPreviewMentionId(mentionId)}
    />
  );
};

export default MentionEventCard;
